import {
  createBrowserRouter,
  Navigate,
  RouteObject,
  RouterProvider,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { Dashboard } from './Dashboard/Dashboard';
import { Subscriptions } from './Subscriptions/Subscriptions';
import { Subscription } from './Subscriptions/Subscription';
import { Root } from './Root';
import { OrganisationRoot } from './OrganisationRoot';
import { GeneralSettings } from './Settings/General/Main/GeneralSettings';
import { EmailTemplates } from './Settings/EmailTemplates/EmailTemplates';
import { EmailPerformance } from './EmailPerformance/EmailPerformance';
import { TeamActivity } from './TeamActivity/TeamActivity';
import { TeamActivityUser } from './TeamActivity/TeamActivityUser';
import { EmailTemplate } from './Settings/EmailTemplates/EmailTemplate';
import { templateGroupSlugs } from './Settings/EmailTemplates/constants';
import { InvoiceActionsTab, InvoicePage } from './Invoice/InvoicePage';
import { InvoiceOverviewV2 } from './Invoice/InvoiceV2/InvoiceOverviewV2';
import { PreviewPolicies } from './Invoice/PreviewPolicies';
import { NotFound } from './NotFound';
import { Login } from './Login';
import { ErrorPage } from './ErrorPage';
import { CRMRoot } from './CRM/CRMRoot';
import { MyAccount } from './Account/MyAccount';
import { SettingsRoot } from './Settings/General/SettingsRoot';
import { Settings } from './Settings/Settings';
import { EmailRoot } from './Settings/General/Email/EmailRoot';
import {
  AdvancedApiTokens,
  AdvancedAutoAssignRules,
  AdvancedConnection,
  AdvancedCreditScore,
  AdvancedDefaultBehaviour,
  AdvancedInvoicePdf,
  AdvancedPaymentPlans,
  AdvancedRoot,
  AdvancedScheduledExports,
  AdvancedWebhooks,
} from './Settings/General/Advanced/AdvancedRoot';
import { AuditLogRoot } from './AuditLogs/AuditLogRoot';
import { PortalRoot } from './Settings/General/Portal/PortalRoot';
import { PaymentRoot } from './Settings/General/Payments/PaymentsRoot';
import { StatementRoot } from './Settings/General/Statement/StatementRoot';
import { GroupV2Root } from './CRM/Group/GroupRoot';
import { ContactActivity } from './CRM/ContactPage/ContactTableSection/ContactActivity';
import { ContactStatements } from './CRM/ContactPage/ContactTableSection/ContactStatement/ContactStatements';
import { ContactPageRoot } from './CRM/ContactPage/ContactPageRoot';
import { ContactOverviewV2 } from './CRM/ContactPage/V2/ContactOverviewV2';
import { OnboardingRoot } from './Onboarding/FullScreen/OnboardingRoot';
import { ActionsTableRoot } from './Actions/ActionTableRoot';
import { GroupActionsTable } from './CRM/Group/GroupTableSection/GroupActionsTable';
import { GroupInsights } from './CRM/Group/GroupInsights';
import { CustomerPaymentsRoot } from './CustomerPayments/CustomerPaymentsRoot';
import { CallsRoot } from './Calls/CallsRoot';
import { FilesRoot } from './Settings/Files/FilesRoot';
import { ApplicableContactPolicies } from './CRM/ContactPage/ContactTableSection/ApplicableContactPolicies';
import { UserSettings } from './Settings/Users/UserSettings';
import { QuotesRoot } from './Quotes/QuotesRoot';
import { QuotePage } from './Quotes/QuotePage';
import { InvoiceAnalytics } from './Invoice/InvoiceAnalytics';
import { GroupPolicies } from './CRM/Group/GroupPoliciesSection/GroupPoliciesRoot';
import { ContactEscalations } from './CRM/ContactPage/ContactTableSection/ContactEscalations';
import { EscalationsRoot } from './Escalations/EscalationsRoot';
import { UserNotificationsRoot } from './UserNotifications/UserNotificationsRoot';
import { EscalationPageRoot } from './Escalations/Escalation/EscalationPageRoot';
import { InvoiceTableRoot } from './Invoices/InvoiceTableRoot';
import { ContactTableRoot } from './CRM/ContactTable/ContactTableRoot';
import { GroupContactsWrapper } from './CRM/Group/GroupTableSection/GroupContactsWrapper';
import { PolicyPageRoot } from './Settings/Policies/PolicyPageRoot';
import { ReportsRoot } from './Reports/ReportsRoot';
import { ContactInvoices } from './CRM/ContactPage/ContactTableSection/ContactInvoices';
import { ContactQuotes } from './CRM/ContactPage/ContactTableSection/ContactQuotes';
import { LateFeeReport } from './Reports/ReportTypes/LateFeeReport';
import { EmailReport } from './Reports/ReportTypes/EmailReport';
import { AgedContactReport } from './Reports/ReportTypes/AgedContactReport';
import { PaymentPlanReport } from './Reports/ReportTypes/PaymentPlanReport';
import { CreditsRoot } from './Credits/CreditsRoot';
import { EscalationNotes } from './Escalations/Escalation/EscalationNotes';
import { ContactCommunications } from './CRM/ContactPage/ContactTableSection/Communications/ContactCommunications';
import { PaymentPlanConfigRoot } from './CRM/Group/PaymentPlanConfig/PaymentPlanConfigRoot';
import { InboxRoot } from './Inbox/InboxRoot';
import { EmailInboxRoute } from './Inbox/EmailInbox/EmailInboxRoute';
import { SmsInboxRoute } from './Inbox/SmsInbox/SmsInboxRoute';
import { AgentsRoot } from './Agents/AgentsRoot';
import { AgentsConversations } from './Agents/Conversations/AgentsConversations';
import { AgentSettingsPage } from './Agents/Settings/AgentSettingsPage';
import { AgentsRules } from './Agents/Settings/AgentsRules';
import { StatementBuilderRoot } from './Settings/General/Statement/Builder/StatementBuilderRoot';

// Route handle read by Root via useMatches. fullWidth exempts wide
// data-table pages from the main content max-width so they use the whole
// screen instead of scrolling horizontally.
export type RouteHandle = {
  fullWidth?: boolean;
};

const fullWidth: RouteHandle = { fullWidth: true };

// Sentry names page-load/navigation transactions after the matched route pattern
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);
const createRoutes = routes => sentryCreateBrowserRouter(routes);

export function Routes() {
  const allRoutes: RouteObject[] = [
    {
      path: `/`,
      element: <Root />,
      errorElement: <ErrorPage />,
      children: [
        {
          path: `account`,
          element: <MyAccount />,
        },
        {
          path: `notifications`,
          element: <UserNotificationsRoot />,
        },
        {
          path: `billing`,
          element: <Subscriptions />,
        },
        {
          path: `billing/subscriptions`,
          element: <Subscriptions />,
        },
        {
          path: `billing/subscriptions/:subscriptionId`,
          element: <Subscription />,
        },
        {
          path: `reports`,
          element: <ReportsRoot />,
          children: [
            {
              path: ``,
              index: true,
              element: <Navigate to={ `aged` }
                replace />,
            },
            {
              path: `aged`,
              element: <AgedContactReport />,
              handle: fullWidth,
            },
            {
              path: `latefee`,
              element: <LateFeeReport />,
              handle: fullWidth,
            },
            {
              path: `email`,
              element: <EmailReport />,
              handle: fullWidth,
            },
            {
              path: `paymentplans`,
              element: <PaymentPlanReport />,
              handle: fullWidth,
            },
          ],
        },
        {
          path: ``,
          element: <OrganisationRoot />,
          children: [
            {
              path: ``,
              index: true,
              element: <Dashboard />,
            },
          ],
        },
        {
          path: `:organisationId`,
          element: <OrganisationRoot />,
          children: [
            {
              path: ``,
              index: true,
              element: <Dashboard />,
            },
            {
              path: `onboarding`,
              element: <OnboardingRoot />,
            },
            {
              path: `invoices`,
              element: <InvoiceTableRoot />,
              handle: fullWidth,
            },
            {
              path: `email-performance`,
              element: <EmailPerformance />,
            },
            {
              path: `team-activity`,
              element: <TeamActivity />,
            },
            {
              path: `team-activity/users/:userId`,
              element: <TeamActivityUser />,
            },
            {
              path: `groups/:groupId`,
              element: <GroupV2Root />,
              children: [
                {
                  path: ``,
                  element: <GroupPolicies />,
                },
                {
                  path: `contacts`,
                  element: <GroupContactsWrapper />,
                  handle: fullWidth,
                },
                {
                  path: `actions`,
                  element: <GroupActionsTable />,
                  handle: fullWidth,
                },
                {
                  path: `insights`,
                  element: <GroupInsights />,
                },
                {
                  path: `paymentplanconfig`,
                  element: <PaymentPlanConfigRoot />,
                },
                {
                  path: `policies/:policyId`,
                  element: <PolicyPageRoot />,
                },
              ],
            },
            {
              path: `contacts`,
              element: <CRMRoot />,
              children: [
                {
                  path: `manage`,
                  element: <ContactTableRoot />,
                  handle: fullWidth,
                },
                {
                  path: `:contactId`,
                  element: <ContactPageRoot />,
                  handle: fullWidth,
                  children: [
                    {
                      path: ``,
                      index: true,
                      element: <ContactOverviewV2 />,
                    },
                    {
                      path: `invoices`,
                      element: <ContactInvoices />,
                    },
                    {
                      path: `quotes`,
                      element: <ContactQuotes />,
                    },
                    {
                      path: `statement`,
                      element: <ContactStatements />,
                    },
                    {
                      path: `comms`,
                      element: <ContactCommunications />,
                    },
                    {
                      path: `activity`,
                      element: <ContactActivity />,
                    },
                    {
                      path: `tasks`, // Different name for navigation highlight
                      element: <ContactEscalations />,
                    },
                    {
                      path: `notes`,
                      element: (
                        // Notes live in the activity feed
                        <Navigate
                          to={ `..` }
                          relative={ `path` }
                          replace
                        />
                      ),
                    },
                    {
                      path: `policies`,
                      element: <ApplicableContactPolicies />,
                    },
                  ],
                },
              ],
            },
            {
              path: `invoices/:invoiceId`,
              element: <InvoicePage />,
              handle: fullWidth,
              children: [
                {
                  path: ``,
                  element: <InvoiceOverviewV2 />,
                },
                {
                  path: `notes`,
                  element: (
                    // Notes live in the activity feed
                    <Navigate
                      to={ `..` }
                      relative={ `path` }
                      replace
                    />
                  ),
                },
                {
                  path: `actions`,
                  element: <InvoiceActionsTab />,
                },
                {
                  path: `policy-preview`,
                  element: <PreviewPolicies />,
                },
                {
                  path: `analytics`,
                  element: <InvoiceAnalytics />,
                },
              ],
            },
            {
              path: `inbox`,
              element: <InboxRoot />,
              children: [
                {
                  path: ``,
                  index: true,
                  element: <Navigate to={ `sms` }
                    replace />,
                },
                {
                  path: `email`,
                  element: <EmailInboxRoute />,
                },
                {
                  path: `sms`,
                  element: <SmsInboxRoute />,
                },
              ],
            },
            {
              path: `actions`,
              element: <ActionsTableRoot />,
              handle: fullWidth,
            },
            {
              path: `escalations`,
              element: <EscalationsRoot />,
              handle: fullWidth,
            },
            {
              // Include contact ID so the useContact hooks etc work and load contact details
              path: `escalations/:escalationId/contact/:contactId`,
              element: <EscalationPageRoot />,
              children: [
                {
                  path: ``,
                  index: true,
                  element: <EscalationNotes />,
                },
                {
                  path: `invoices`,
                  element: <ContactInvoices />,
                },
                {
                  path: `statement`,
                  element: <ContactStatements />,
                },
                {
                  path: `comms`,
                  element: <ContactCommunications />,
                },
                {
                  path: `activity`,
                  element: <ContactActivity />,
                },
                {
                  path: `tasks`, // Different name for navigation highlight
                  element: <ContactEscalations />,
                },
              ],
            },
            {
              path: `quotes`,
              element: <QuotesRoot />,
              handle: fullWidth,
            },
            {
              path: `quotes/:quoteId`,
              element: <QuotePage />,
            },
            {
              path: `credits`,
              element: <CreditsRoot />,
              handle: fullWidth,
            },
            {
              path: `agents`,
              element: <AgentsRoot />,
              children: [
                {
                  path: ``,
                  index: true,
                  element: <Navigate to={ `conversations` }
                    replace />,
                },
                {
                  path: `conversations`,
                  element: <AgentsConversations />,
                },
                {
                  path: `conversations/:conversationId`,
                  element: <AgentsConversations />,
                },
                {
                  path: `rules`,
                  element: <AgentsRules />,
                },
                {
                  path: `agent-settings`,
                  element: <AgentSettingsPage />,
                },
              ],
            },
            {
              path: `payments`,
              element: <CustomerPaymentsRoot />,
              handle: fullWidth,
            },
            {
              path: `calls`,
              element: <CallsRoot />,
              handle: fullWidth,
            },
            {
              path: `settings`,
              element: <Settings />,
              children: [

                {
                  path: `general`,
                  element: <SettingsRoot />,
                  children: [
                    {
                      path: ``,
                      index: true,
                      element: <GeneralSettings />,
                    },
                    {
                      path: `email`,
                      element: <EmailRoot />,
                    },
                    {
                      path: `statement`,
                      element: <StatementRoot />,
                    },
                    {
                      path: `statement-builder`,
                      element: <StatementBuilderRoot />,
                      handle: fullWidth,
                    },
                    {
                      path: `portal`,
                      element: <PortalRoot />,
                    },
                    {
                      path: `payments`,
                      element: <PaymentRoot />,
                    },
                    {
                      path: `advanced`,
                      element: <AdvancedRoot />,
                      children: [
                        {
                          path: ``,
                          index: true,
                          element: <AdvancedDefaultBehaviour />,
                        },
                        {
                          path: `auto-assign`,
                          element: <AdvancedAutoAssignRules />,
                        },
                        {
                          path: `webhooks`,
                          element: <AdvancedWebhooks />,
                        },
                        {
                          path: `payment-plans`,
                          element: <AdvancedPaymentPlans />,
                        },
                        {
                          path: `invoice-pdf`,
                          element: <AdvancedInvoicePdf />,
                        },
                        {
                          path: `scheduled-exports`,
                          element: <AdvancedScheduledExports />,
                        },
                        {
                          path: `api-tokens`,
                          element: <AdvancedApiTokens />,
                        },
                        {
                          path: `credit-score`,
                          element: <AdvancedCreditScore />,
                        },
                        {
                          path: `connection`,
                          element: <AdvancedConnection />,
                        },
                      ],
                    },
                  ],
                },
                {
                  path: `email-templates`,
                  element: <EmailTemplates />,
                },
                // One static route per type filter; static segments outrank
                // the :templateId detail route so these can't clash with it
                ...Object.values(templateGroupSlugs).map(slug => ({
                  path: `email-templates/${slug}`,
                  element: <EmailTemplates />,
                })),
                {
                  path: `files`,
                  element: <FilesRoot />,
                },
                {
                  path: `email-templates/:templateId`,
                  element: <EmailTemplate />,
                },
                {
                  path: `users`,
                  element: <UserSettings />,
                },
              ],
            },
            {
              path: `logs`,
              element: <AuditLogRoot />,
            },
          ],
        },
      ],
    },
    {
      // Outside <Root /> so the unauthenticated user fetch doesn't trigger its own login redirect first
      path: `login`,
      element: <Login />,
    },
    {
      path: `*`,
      element: <NotFound />,
    },
  ];

  return (
    <RouterProvider router={ createRoutes(allRoutes) } />
  );
}
